import { useMutation, type UseMutationOptions } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { ensureExists, exists, isAssigned } from '~/utils'

import {
  type BrandSettingsInputBrand,
  type BrandSettingsInputBrandDefault,
} from '../utils'

type UpdateBrandsMutationVariables = {
  brands: BrandSettingsInputBrand[]
  defaultBrands: BrandSettingsInputBrandDefault[]
  surveyGroupId: string
}

const createBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_CreateBrandBulk(
    $surveyGroupId: String!
    $input: [SurveyGroupBrandBulkCreateItemInput!]!
  ) {
    createSurveyGroupBrandBulk(surveyGroupId: $surveyGroupId, input: $input) {
      result
    }
  }
`)

const updateBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_UpdateBrandBulk(
    $input: [SurveyGroupBrandBulkUpdateItemInput!]!
  ) {
    updateSurveyGroupBrandBulk(input: $input) {
      result
    }
  }
`)

const deleteBulkDocument = graphql(`
  mutation TimeSeriesSettingsPage_DeleteBrandBulk($brandIds: [String!]!) {
    deleteSurveyGroupBrandBulk(surveyGroupBrandIds: $brandIds) {
      result
    }
  }
`)

export const useUpdateBrandsMutation = (
  options: Omit<
    UseMutationOptions<void, unknown, UpdateBrandsMutationVariables, unknown>,
    'mutationFn'
  > = {},
) => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()

  return useMutation({
    mutationFn: async ({ brands, defaultBrands, surveyGroupId }) => {
      // 削除
      const brandIdsToDelete = R.differenceWith(
        defaultBrands,
        brands,
        (defaultBrand, brand) => defaultBrand.id === brand.id,
      ).map(R.prop('id'))
      if (0 < brandIdsToDelete.length) {
        await graphQLRequest({
          document: deleteBulkDocument,
          variables: { brandIds: brandIdsToDelete },
        })
      }

      // 作成
      const brandsToCreate = brands.filter((brand) => !exists(brand.id))
      if (0 < brandsToCreate.length) {
        await graphQLRequest({
          document: createBulkDocument,
          variables: {
            surveyGroupId,
            input: brandsToCreate.map((brand) => ({
              name: brand.name,
              surveyBrands: R.entries(brand.attachedBrandMap)
                .map(([surveyId, brandId]) => ({ surveyId, brandId }))
                .filter(
                  (
                    surveyBrand,
                  ): surveyBrand is {
                    surveyId: string
                    brandId: number
                  } => isAssigned(surveyBrand.brandId),
                ),
            })),
          },
        })
      }

      // 更新
      const brandsToUpdate = R.differenceWith(
        brands.filter((brand) => exists(brand.id)),
        defaultBrands,
        (brand, defaultBrand) =>
          brand.id === defaultBrand.id &&
          brand.name === defaultBrand.name &&
          R.isDeepEqual(brand.attachedBrandMap, defaultBrand.attachedBrandMap),
      )
      if (0 < brandsToUpdate.length) {
        await graphQLRequest({
          document: updateBulkDocument,
          variables: {
            input: brandsToUpdate.map((brand) => ({
              surveyGroupBrandId: ensureExists(brand.id, t('assertion_failed')),
              surveyBrands: R.entries(brand.attachedBrandMap)
                .map(([surveyId, brandId]) => ({ surveyId, brandId }))
                .filter(
                  (
                    surveyBrand,
                  ): surveyBrand is {
                    surveyId: string
                    brandId: number
                  } => isAssigned(surveyBrand.brandId),
                ),
              name: brand.name,
            })),
          },
        })
      }
    },
    ...options,
  })
}
